import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bikeVariantAPI, BASE_URL } from '../services/api';
import './Brands.css';

const BikeVariantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const [activeSpecTab, setActiveSpecTab] = useState(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchVariantDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchVariantDetails = async () => {
    try {
      setLoading(true);
      const response = await bikeVariantAPI.getById(id);
      setVariant(response.data);

      // Set default tabs
      if (response.data.specs && response.data.specs.length > 0) {
        const categories = [...new Set(response.data.specs.map(s => s.category))];
        setActiveSpecTab(categories[0]);
      }

      setError('');
    } catch (error) {
      console.error('Error fetching variant details:', error);
      setError('Failed to load variant details');
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const handleEditClick = () => {
    setEditFormData({
      name: variant.name || '',
      price: variant.price || '',
      image: variant.image || '',
      url: variant.url || ''
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await bikeVariantAPI.update(id, editFormData);
      setShowEditModal(false);
      fetchVariantDetails();
    } catch (err) {
      console.error('Error updating variant:', err);
      setError(err.response?.data?.error || 'Failed to update variant');
    } finally {
      setEditLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    if (typeof price === 'string') {
      if (price.includes('Lakh') || price.includes('Crore') || price.includes('Rs')) {
        return price;
      }
      const numPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
      if (!isNaN(numPrice) && numPrice > 0) {
        if (numPrice >= 10000000) {
          return `Rs. ${(numPrice / 10000000).toFixed(2)} Crore`;
        } else if (numPrice >= 100000) {
          return `Rs. ${(numPrice / 100000).toFixed(2)} Lakh`;
        }
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(numPrice);
      }
      return price;
    }
    if (typeof price === 'number') {
      if (price >= 10000000) {
        return `Rs. ${(price / 10000000).toFixed(2)} Crore`;
      } else if (price >= 100000) {
        return `Rs. ${(price / 100000).toFixed(2)} Lakh`;
      }
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
    }
    return '-';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath}`;
  };

  const getVariantColors = () => {
    if (variant?.colors && Array.isArray(variant.colors) && variant.colors.length > 0) {
      return variant.colors;
    }
    return [];
  };

  // Group specs by category
  const getSpecsByCategory = () => {
    if (!variant?.specs || variant.specs.length === 0) return {};
    return variant.specs.reduce((acc, spec) => {
      if (!acc[spec.category]) acc[spec.category] = [];
      acc[spec.category].push(spec);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <div className="brands-page">
        <div className="loading">Loading variant details...</div>
      </div>
    );
  }

  if (error || !variant) {
    return (
      <div className="brands-page">
        <div className="alert alert-error">{error || 'Variant not found'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/bike-variants')}>
          Back to Variants
        </button>
      </div>
    );
  }

  const colors = getVariantColors();
  const selectedColor = colors[selectedColorIdx] || null;
  // Show color image if selected, otherwise show main variant image or first gallery image
  const displayImage = selectedColor?.image || variant.image || (variant.images && variant.images.length > 0 ? variant.images[0].image : null) || variant.bike?.image;
  const specsByCategory = getSpecsByCategory();
  const specCategories = Object.keys(specsByCategory);

  return (
    <div className="brands-page">
      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Bike Variant</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Variant Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Price</label>
                  <input
                    type="text"
                    name="price"
                    value={editFormData.price}
                    onChange={handleEditFormChange}
                    placeholder="e.g., 1.5 Lakh"
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="text"
                    name="image"
                    value={editFormData.image}
                    onChange={handleEditFormChange}
                    placeholder="e.g., /uploads/bikes/variant.jpg"
                  />
                </div>
                <div className="form-group">
                  <label>Source URL</label>
                  <input
                    type="text"
                    name="url"
                    value={editFormData.url}
                    onChange={handleEditFormChange}
                    placeholder="e.g., /bikes/hero/splendor-plus"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header">
        <div className="breadcrumb">
          <button className="btn btn-link" onClick={() => navigate('/bike-variants')}>
            Bike Variants
          </button>
          <span className="breadcrumb-separator">/</span>
          <span>{variant.name}</span>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleEditClick}>
            Edit Variant
          </button>
        </div>
      </div>

      <div className="details-container">
        {/* Main Info Card */}
        <div className="details-card details-main">
          <div className="details-image-section">
            <div className="details-image">
              {displayImage ? (
                <img
                  src={getImageUrl(displayImage)}
                  alt={`${variant.name}${selectedColor ? ` - ${selectedColor.name}` : ''}`}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="no-image-placeholder large">No Image Available</div>
              )}
            </div>

            {/* Color Swatches */}
            {colors.length > 0 && (
              <div className="details-colors">
                <h4>Available Colors ({colors.length})</h4>
                <div className="color-swatches-large">
                  {colors.map((color, idx) => {
                    const isSelected = selectedColorIdx === idx;
                    const colorHex = color.code ? (color.code.startsWith('#') ? color.code : `#${color.code}`) : '#888';
                    // Check if it's a dual color (stored as JSON string)
                    let isDualColor = false;
                    let dualColors = null;
                    if (color.type === 'dual' && color.code) {
                      try {
                        dualColors = JSON.parse(color.code);
                        isDualColor = Array.isArray(dualColors) && dualColors.length >= 2;
                      } catch (e) {
                        // Not a JSON array, treat as single color
                      }
                    }

                    return (
                      <div
                        key={idx}
                        className={`color-swatch-large ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedColorIdx(idx)}
                        title={color.name}
                      >
                        <div
                          className="color-circle"
                          style={{
                            backgroundColor: isDualColor ? dualColors[0] : colorHex,
                            backgroundImage: isDualColor
                              ? `linear-gradient(135deg, ${dualColors[0]} 50%, ${dualColors[1]} 50%)`
                              : 'none'
                          }}
                        />
                        <span className="color-name">{color.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="details-info-section">
            <div className="details-header">
              <h1>{variant.name}</h1>
              <p className="details-subtitle">
                {variant.bike?.brand?.brandName} {variant.bike?.name}
              </p>
            </div>

            <div className="details-price-section">
              <div className="price-box">
                <span className="price-label">Price</span>
                <span className="price-value primary">{formatPrice(variant.price)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Specifications Card - from API */}
        {specCategories.length > 0 && (
          <div className="details-card">
            <h3>Specifications</h3>
            <div className="category-tabs">
              {specCategories.map(category => (
                <button
                  key={category}
                  className={`category-tab ${activeSpecTab === category ? 'active' : ''}`}
                  onClick={() => setActiveSpecTab(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="specs-grid">
              {activeSpecTab && specsByCategory[activeSpecTab]?.map((spec, idx) => (
                <div key={idx} className="spec-row">
                  <span className="spec-label">{spec.specName}</span>
                  <span className="spec-value">{spec.specValue || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery Card */}
        {variant.images && variant.images.length > 0 && (
          <div className="details-card">
            <h3>Gallery ({variant.images.length})</h3>
            <div className="gallery-grid">
              {variant.images.map((img, idx) => (
                <div key={idx} className="gallery-item">
                  <img
                    src={getImageUrl(img.image)}
                    alt={img.alt || `${variant.name} image ${idx + 1}`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Info Card */}
        <div className="details-card">
          <h3>Additional Information</h3>
          <div className="specs-grid">
            <div className="spec-row">
              <span className="spec-label">Variant ID</span>
              <span className="spec-value">{variant.variantId || variant.id}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label">Slug</span>
              <span className="spec-value">{variant.slug}</span>
            </div>
            {variant.url && (
              <div className="spec-row">
                <span className="spec-label">Source URL</span>
                <span className="spec-value">
                  <a href={`https://www.bikedekho.com${variant.url}`} target="_blank" rel="noopener noreferrer">
                    View on BikeDekho
                  </a>
                </span>
              </div>
            )}
            <div className="spec-row">
              <span className="spec-label">Created At</span>
              <span className="spec-value">{new Date(variant.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label">Updated At</span>
              <span className="spec-value">{new Date(variant.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BikeVariantDetails;
